import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContentModal } from '../UnifiedModal';
import SocialShareButtons from '../SocialShareButtons';
import { usePublicationShare } from '../../hooks/usePublicationShare';
import { generateSharePassword } from './sharePassword';
import {
  faGlobe,
  faLock,
  faCopy,
  faCheck,
  faArrowRight,
  faShieldAlt,
  faCode,
} from '@fortawesome/free-solid-svg-icons';
import './ShareStrategyModal.css';

const EMPTY_STRATEGY_OPTIONS = [];

const LEGACY_DEFAULT_PERMISSIONS = Object.freeze({
  allowPerformanceView: true,
  allowPositionsView: true,
  allowFilledTradesView: true,
  allowOpenOrdersView: false,
  allowExactQuantitiesView: false,
  allowCodeView: false,
  allowClone: false,
});

/**
 * What the toggles show before the owner changes anything.
 * - A publication that already exists keeps exactly its stored policy.
 * - A new strategy private link shares the owner's backtest results, but not
 *   the code and not copies (Rob, 2026-09-29; this replaces the 2026-09-23
 *   code-and-copies-on default). The recipient page calls allowPerformanceView
 *   "Backtest results". The owner can still turn any of them on or off.
 * - Marketplace listings stay code-protected, and account shares never offer
 *   code or cloning.
 * The server still stores deny-by-default; these are explicit grants the owner saves.
 */
function initialSharePermissions({ basePolicy, hasStoredPublication, shareType, isAccountShare }) {
  if (hasStoredPublication) return basePolicy;
  if (isAccountShare) return { ...basePolicy, allowCodeView: false, allowClone: false };
  if (shareType === 'private') {
    return { ...basePolicy, allowPerformanceView: true, allowCodeView: false, allowClone: false };
  }
  return { ...basePolicy, allowCodeView: false, allowClone: false };
}

const LIVE_LINK_SAVED = 'Settings saved. Your link did not change.';

/**
 * Zoom style: the password rides in the link's #pwd= fragment, which browsers
 * never send to a server, a log, or a Referer. The server already builds it in;
 * this covers older callers that return the bare link.
 */
function withLinkPassword(url, password) {
  if (!url || !password || url.includes('#pwd=')) return url;
  return `${url}#pwd=${encodeURIComponent(password)}`;
}

/**
 * @typedef {Object} ShareStrategyModalStrategyOption
 * @property {string} id
 * @property {string} name
 * @property {string=} status
 */

/**
 * @typedef {Object} ShareStrategyModalProps
 * @property {boolean} show
 * @property {() => void} onHide
 * @property {string} strategyName
 * @property {string | null=} accountId
 * @property {string | null=} strategyId
 * @property {ShareStrategyModalStrategyOption[]=} strategyOptions
 * @property {((strategyId: string) => void)=} onStrategyChange
 * @property {(() => Promise<string | null>)=} getApiToken
 * @property {boolean=} isPublic
 * @property {string=} shareUrl
 * @property {Function=} onMakePublic
 * @property {Function=} onMakePrivate
 * @property {boolean=} isInMarketplace
 * @property {string | null=} marketplaceListingId
 * @property {boolean=} isOwner
 * @property {{ shareUrl?: string } | null=} existingPrivateShare
 * @property {boolean=} shareInfoLoading
 * @property {Function=} onRefreshShareInfo
 * @property {"strategy" | "account"=} subjectType
 * @property {string=} accountName
 * @property {ShareStrategyModalStrategyOption[]=} availableStrategies
 * @property {string[]=} initialSelectedStrategyIds
 */

/** @param {ShareStrategyModalProps} props */
const ShareStrategyModal = ({
  show,
  onHide,
  strategyName: legacySubjectName,
  accountId = /** @type {string | null | undefined} */ (undefined),
  strategyId = /** @type {string | null | undefined} */ (undefined),
  strategyOptions: _strategyOptions = EMPTY_STRATEGY_OPTIONS,
  onStrategyChange: _onStrategyChange = /** @type {((strategyId: string) => void) | undefined} */ (
    undefined
  ),
  getApiToken = /** @type {(() => Promise<string | null>) | undefined} */ (undefined),
  isPublic: legacyIsPublic = false,
  shareUrl: legacyShareUrl = '',
  onMakePublic: legacyOnMakePublic = /** @type {Function | undefined} */ (undefined),
  onMakePrivate: legacyOnMakePrivate = /** @type {Function | undefined} */ (undefined),
  isInMarketplace: legacyIsInMarketplace = false,
  marketplaceListingId: legacyMarketplaceListingId = /** @type {string | null | undefined} */ (
    undefined
  ),
  isOwner = true,
  existingPrivateShare:
    legacyExistingPrivateShare = /** @type {{shareUrl: string} | null | undefined} */ (undefined),
  shareInfoLoading: legacyShareInfoLoading = false,
  onRefreshShareInfo: legacyOnRefreshShareInfo = /** @type {Function | undefined} */ (undefined),
  subjectType = 'strategy',
  accountName = undefined,
  availableStrategies: _availableStrategies = EMPTY_STRATEGY_OPTIONS,
  initialSelectedStrategyIds: _initialSelectedStrategyIds = EMPTY_STRATEGY_OPTIONS,
}) => {
  const usesCanonicalPublication =
    isOwner && Boolean(accountId || strategyId) && Boolean(getApiToken);
  const publication = usePublicationShare(
    { subjectKind: subjectType, accountId, strategyId },
    show && usesCanonicalPublication,
    getApiToken,
  );
  const canonicalState = usesCanonicalPublication ? publication.state : null;
  const strategyName = canonicalState?.subject?.name || legacySubjectName || 'Shared publication';
  const isInMarketplace = canonicalState?.marketplace
    ? canonicalState.marketplace.isShared
    : legacyIsInMarketplace;
  const isPublic = canonicalState?.marketplace
    ? canonicalState.marketplace.isShared
    : legacyIsPublic;
  const marketplaceListingId = canonicalState?.marketplace
    ? canonicalState.marketplace.listingId
    : legacyMarketplaceListingId;
  const shareUrl = canonicalState?.marketplace?.url || legacyShareUrl;
  const existingPrivateShare = canonicalState?.privateLink
    ? canonicalState.privateLink.isActive && canonicalState.privateLink.url
      ? { shareUrl: canonicalState.privateLink.url }
      : null
    : legacyExistingPrivateShare;
  const privateLinkActive = canonicalState?.privateLink?.isActive || Boolean(existingPrivateShare);
  const shareInfoLoading = usesCanonicalPublication ? publication.loading : legacyShareInfoLoading;
  const onRefreshShareInfo = usesCanonicalPublication
    ? publication.refresh
    : legacyOnRefreshShareInfo;
  const onMakePrivate = usesCanonicalPublication
    ? publication.removeMarketplace
    : legacyOnMakePrivate;
  const onMakePublic = usesCanonicalPublication
    ? (_unused, requestedShareType, options) =>
        requestedShareType === 'private'
          ? publication.makePrivateLink(options)
          : publication.makePublic(options)
    : legacyOnMakePublic;
  const [copied, setCopied] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareType, setShareType] = useState(null);
  const [privateShareCreated, setPrivateShareCreated] = useState(false);
  const [privateShareUrl, setPrivateShareUrl] = useState('');
  const [forceRegenerate, setForceRegenerate] = useState(false);
  // A private link is password protected by default, Zoom style (Rob,
  // 2026-09-29): a fresh strong password is built into the link itself
  // (#pwd=...), so the full link opens directly and nobody copies a password
  // separately. The owner never has to see it. Turning this off shares openly.
  const [passwordProtected, setPasswordProtected] = useState(true);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [privateExpiresAt, setPrivateExpiresAt] = useState('');
  const [privateRecipientEmail, setPrivateRecipientEmail] = useState('');
  // Saving a live link's settings in place, and the confirmation after it.
  const [savingLiveLink, setSavingLiveLink] = useState(false);
  const [liveLinkSaved, setLiveLinkSaved] = useState(false);
  const [marketplacePresentation, setMarketplacePresentation] = useState({
    title: '',
    description: '',
    details: '',
    tags: '',
    assetClasses: '',
  });
  const [presentationImage, setPresentationImage] = useState(null);
  const [removePresentationImage, setRemovePresentationImage] = useState(false);
  const [presentationDirty, setPresentationDirty] = useState(false);
  // Track when we just published to marketplace (before prop updates)
  const [justPublished, setJustPublished] = useState(false);
  const [justPublishedListingId, setJustPublishedListingId] = useState(null);
  const marketplaceUrl =
    canonicalState?.marketplace?.url ||
    (marketplaceListingId
      ? `/marketplace/strategy/${marketplaceListingId}`
      : justPublishedListingId
        ? `/marketplace/strategy/${justPublishedListingId}`
        : shareUrl || '');
  // Track when strategy was just made private (unpublished)
  const [justMadePrivate, setJustMadePrivate] = useState(false);
  const wasOpenRef = React.useRef(false);
  const [permissions, setPermissions] = useState(LEGACY_DEFAULT_PERMISSIONS);
  // Once the owner flips a toggle, defaults never overwrite their choice.
  const [permissionsTouched, setPermissionsTouched] = useState(false);
  const isAccountShare = subjectType === 'account';
  const subjectName = isAccountShare ? accountName || strategyName || 'Live account' : strategyName;
  const modalTitle = isAccountShare ? 'Share Account' : 'Share Strategy';
  const publicDestinationLabel = 'Marketplace';

  // Reset state only across an actual modal boundary. Canonical publication
  // state can arrive after the owner has already selected a destination; that
  // background refresh must never overwrite the in-progress choice.
  React.useEffect(() => {
    if (!show) {
      // Reset everything when modal closes
      setCopied(false);
      setShareType(null);
      setIsToggling(false); // Reset loading state
      setShareError('');
      setPrivateShareCreated(false);
      setPrivateShareUrl('');
      setForceRegenerate(false);
      setPasswordProtected(true);
      setPasswordTouched(false);
      setPrivateExpiresAt('');
      setPrivateRecipientEmail('');
      setSavingLiveLink(false);
      setLiveLinkSaved(false);
      setPresentationImage(null);
      setRemovePresentationImage(false);
      setPresentationDirty(false);
      setJustPublished(false);
      setJustPublishedListingId(null);
      setJustMadePrivate(false);
    } else if (!wasOpenRef.current) {
      // Every owner entry point opens the same two-choice flow. Existing status
      // is shown on each choice so the user never has to remember what they did.
      setShareType(isInMarketplace ? 'public' : null);
      setIsToggling(false);
      setShareError('');
      setForceRegenerate(false);
    }
    wasOpenRef.current = show;
  }, [show, isInMarketplace, isAccountShare]);

  // Defaults only apply to a brand new share. An existing publication, private
  // link, or listing keeps its settings, so rotating a link never widens access.
  const hasStoredPublication = usesCanonicalPublication
    ? Boolean(canonicalState?.publicationId)
    : Boolean(existingPrivateShare?.shareUrl) || Boolean(isInMarketplace);
  React.useEffect(() => {
    if (!show) {
      setPermissionsTouched(false);
      return;
    }
    if (permissionsTouched) return;
    setPermissions(
      initialSharePermissions({
        basePolicy: canonicalState?.policy || LEGACY_DEFAULT_PERMISSIONS,
        hasStoredPublication,
        shareType,
        isAccountShare,
      }),
    );
  }, [
    show,
    canonicalState?.policy,
    hasStoredPublication,
    shareType,
    isAccountShare,
    permissionsTouched,
  ]);

  // A new link starts protected; a live link shows whether it has a password.
  const storedPasswordRequired = canonicalState?.privateLink?.passwordRequired;
  React.useEffect(() => {
    if (!show || passwordTouched) return;
    setPasswordProtected(privateLinkActive ? storedPasswordRequired !== false : true);
  }, [show, passwordTouched, privateLinkActive, storedPasswordRequired]);

  React.useEffect(() => {
    if (!show || !isInMarketplace) return;
    setShareType((current) => current ?? 'public');
  }, [show, isInMarketplace]);

  React.useEffect(() => {
    if (!show || !canonicalState?.marketplace || presentationDirty) return;
    setMarketplacePresentation({
      title: canonicalState.marketplace.title || subjectName,
      description: canonicalState.marketplace.description || '',
      details: canonicalState.marketplace.details || '',
      tags: (canonicalState.marketplace.tags || []).join(', '),
      assetClasses: (canonicalState.marketplace.assetClasses || []).join(', '),
    });
  }, [canonicalState?.marketplace, presentationDirty, show, subjectName]);

  React.useEffect(() => {
    if (publication.error) setShareError(publication.error);
  }, [publication.error]);

  const copyUrlToClipboard = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleCopyUrl = () => {
    const urlToCopy = privateShareCreated
      ? privateShareUrl
      : shareType === 'private' && existingPrivateShare?.shareUrl
        ? existingPrivateShare.shareUrl
        : shareUrl;
    copyUrlToClipboard(urlToCopy);
  };

  const handleToggleVisibility = async (shouldForceRegenerate = false) => {
    setIsToggling(true);
    setShareError('');
    try {
      // Zoom-style: No password required - backend auto-generates secure token
      // Persist an explicit capability set before creating either grant.
      const options = {
        forceRegenerate: shouldForceRegenerate || forceRegenerate,
        allowClone: permissions.allowClone,
      };
      // Every new or regenerated protected link gets a fresh password. Only
      // its hash is stored, so an old password could never be rebuilt into a
      // new link.
      if (shareType === 'private' && passwordProtected) options.password = generateSharePassword();
      else if (shareType === 'private' && privateLinkActive && storedPasswordRequired) {
        options.password = null;
      }
      if (shareType === 'private' && privateExpiresAt) {
        options.expiresAt = new Date(privateExpiresAt).toISOString();
      }
      if (shareType === 'private' && privateRecipientEmail) {
        options.recipientEmail = privateRecipientEmail.trim();
      }
      if (usesCanonicalPublication) {
        if (shareType === 'public' && presentationDirty) {
          const presentation = {
            title: marketplacePresentation.title.trim() || subjectName,
            description: marketplacePresentation.description.trim() || null,
            details: marketplacePresentation.details.trim() || null,
            tags: marketplacePresentation.tags
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean),
            assetClasses: marketplacePresentation.assetClasses
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean),
          };
          if (removePresentationImage) presentation.heroImageUrl = null;
          const updated = await publication.updatePresentation(presentation);
          if (presentationImage) {
            await publication.uploadHeroImage(presentationImage, updated.version);
          }
        }
        await publication.updatePolicy({
          ...permissions,
          allowCodeView: isAccountShare ? false : permissions.allowCodeView,
          allowClone: isAccountShare ? false : permissions.allowClone,
        });
      }
      if (!onMakePublic) throw new Error('Sharing is not available from this page.');
      const result = await onMakePublic(undefined, shareType, options);

      if (result === false) {
        setIsToggling(false);
        setShareError('Sharing could not be updated. Please try again.');
        return;
      }

      // Check if this was a private share creation
      if (result?.isPrivate && result?.success && result?.shareUrl) {
        // Private share created - show success state with auto-generated link
        setPrivateShareCreated(true);
        setPrivateShareUrl(withLinkPassword(result.shareUrl, options.password));
        setIsToggling(false);
        if (!usesCanonicalPublication && onRefreshShareInfo) {
          onRefreshShareInfo();
        }
      } else if (shareType === 'private' && result?.shareUrl) {
        // Alternative check for private shares
        setPrivateShareCreated(true);
        setPrivateShareUrl(withLinkPassword(result.shareUrl, options.password));
        setIsToggling(false);
      } else if (
        result?.success &&
        (result?.moderationStatus === 'approved' || result?.isPublic === true)
      ) {
        const listingId = result.marketplaceListingId || marketplaceListingId;
        setJustPublished(true);
        setJustPublishedListingId(listingId || null);
        setIsToggling(false);
        if (!usesCanonicalPublication && onRefreshShareInfo) {
          onRefreshShareInfo();
        }
      } else {
        // Fallback - reset loading state
        setIsToggling(false);
      }
    } catch (error) {
      // Error is handled by parent component with toast
      console.error('[ShareStrategyModal] Error:', error);
      setIsToggling(false);
      setShareError(error?.message || 'Sharing could not be updated. Please try again.');
      return false;
    }
  };

  // Saves what a live private link shares without issuing a new URL (Rob,
  // 2026-09-23). The recipient sees the new settings the next time they load
  // the same link. A new password, expiration, or recipient still needs a new
  // link, because the server issues a new secret for those.
  const handleSaveLiveLinkSettings = async () => {
    setSavingLiveLink(true);
    setShareError('');
    setLiveLinkSaved(false);
    try {
      await publication.updatePolicy({
        ...permissions,
        allowCodeView: isAccountShare ? false : permissions.allowCodeView,
        allowClone: isAccountShare ? false : permissions.allowClone,
      });
      setLiveLinkSaved(true);
    } catch (error) {
      setShareError(error?.message || 'Settings could not be saved. Please try again.');
    } finally {
      setSavingLiveLink(false);
    }
  };

  const handleMakePrivate = async () => {
    // CRITICAL: onMakePrivate MUST be provided - no fallback allowed
    // Previous fallback to onMakePublic caused silent bugs where clicking
    // The removal action would publish instead of unpublish
    if (!onMakePrivate) {
      console.error(
        '[ShareStrategyModal] CRITICAL: onMakePrivate prop is required but not provided!',
      );
      alert('Unable to make strategy private - configuration error. Please report this bug.');
      return false;
    }

    setIsToggling(true);
    setShareError('');
    try {
      const result = await onMakePrivate();
      if (result === false) {
        // Error already handled by parent component with toast
        setIsToggling(false);
        return;
      }
      // Success - show success state instead of closing modal
      setIsToggling(false);
      setJustMadePrivate(true);
    } catch (error) {
      // Error is handled by parent component with toast
      console.error('[ShareStrategyModal] Error during makePrivate:', error);
      setIsToggling(false);
      setShareError(
        error?.message ||
          `The ${publicDestinationLabel} sharing could not be removed. Please try again.`,
      );
      return false;
    }
  };

  // A live canonical private link can take new settings in place.
  const canSaveLiveLink = usesCanonicalPublication && shareType === 'private' && privateLinkActive;
  const passwordChanged =
    privateLinkActive && passwordProtected !== (storedPasswordRequired !== false);
  const accessChangeNeedsNewLink = Boolean(
    passwordChanged || privateExpiresAt || privateRecipientEmail,
  );

  return (
    <ContentModal
      isOpen={show}
      onClose={onHide}
      size="md"
      className="share-strategy-modal"
      title={modalTitle}
      ariaLabel={modalTitle}
      zIndex={10050}
    >
      {!isOwner ? (
        <>
          {/* Non-owner view - show marketplace link + social share (no publish/unpublish) */}
          <div className="share-link-section">
            <div className="strategy-header">
              <div className="strategy-name">{subjectName}</div>
              <div className="strategy-subtitle">
                Share this {isAccountShare ? 'account' : 'strategy'} with others
              </div>
            </div>

            <div className="share-link-container">
              <label className="link-label">Marketplace link</label>
              <div className="link-input-group">
                <input type="text" value={shareUrl} readOnly className="link-input" />
                <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopyUrl}>
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  <span>{copied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>

            {/* Social share buttons for non-owners */}
            {shareUrl && (
              <SocialShareButtons
                url={shareUrl}
                text={`Check out "${strategyName}" on BotSpot Marketplace!`}
                className="compact"
              />
            )}

            <div className="share-info">
              <FontAwesomeIcon icon={faGlobe} className="me-2" />
              <span className="text-muted">
                This {isAccountShare ? 'account' : 'strategy'} is available in the{' '}
                {isAccountShare ? 'Marketplace' : 'marketplace'}
              </span>
            </div>
          </div>
        </>
      ) : justMadePrivate ? (
        <>
          {/* Success State - Just Made Private - Clean text only */}
          <div className="private-removed-state">
            <h5 className="private-removed-title">Removed from {publicDestinationLabel}</h5>
            <p className="private-removed-desc">
              {isAccountShare ? (
                <>
                  <strong>"{subjectName}"</strong> is now private.
                </>
              ) : (
                <>
                  <strong>"{subjectName}"</strong> is now private and no longer visible to other
                  users.
                </>
              )}
            </p>
          </div>

          {/* Action button */}
          <div className="modal-action-buttons">
            <Button variant="primary" onClick={onHide} size="sm">
              Done
            </Button>
          </div>
        </>
      ) : justPublished || privateShareCreated ? (
        <>
          {isInMarketplace && !justPublished && !privateShareCreated && (
            <div className="share-type-pills">
              <button
                type="button"
                className="share-pill active"
                onClick={() => setShareType('public')}
              >
                <FontAwesomeIcon icon={faGlobe} className="pill-icon" />
                <span className="pill-label">{publicDestinationLabel}</span>
                <span className="share-pill-status">Shared</span>
              </button>
              <button type="button" className="share-pill" onClick={() => setShareType('private')}>
                <FontAwesomeIcon icon={faLock} className="pill-icon" />
                <span className="pill-label">Private Link</span>
                {existingPrivateShare?.shareUrl && (
                  <span className="share-pill-status">Link ready</span>
                )}
              </button>
            </div>
          )}
          {/* Success State */}
          <div className="success-state">
            {/* Marketplace success */}
            {(isInMarketplace || justPublished) && !privateShareCreated && (
              <>
                <div className="marketplace-success-status" role="status">
                  <span className="marketplace-success-status-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <span>
                    {isAccountShare ? 'Marketplace sharing active' : 'Marketplace listing active'}
                  </span>
                </div>
                <h5 className="success-title">
                  {isAccountShare
                    ? 'Your account is shared.'
                    : 'You are now shared to the Marketplace.'}
                </h5>
                <p className="success-desc">
                  {isAccountShare
                    ? `"${subjectName}" is now available in Marketplace.`
                    : `"${subjectName}" is now available in the Marketplace.`}
                </p>
                {isAccountShare ? (
                  <a
                    href={marketplaceUrl}
                    className="marketplace-view-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Marketplace
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </a>
                ) : (
                  (marketplaceListingId || justPublishedListingId) && (
                    <a
                      href={marketplaceUrl}
                      className="marketplace-view-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View your listing
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    </a>
                  )
                )}
              </>
            )}
            {/* Private share success - simple and clean */}
            {privateShareCreated && (
              <>
                <div className="success-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <h5 className="success-title">Link Created</h5>
                <p className="success-desc">
                  Share this link with anyone you want to give access to "{subjectName}"
                </p>
              </>
            )}
          </div>

          {/* Share Link - Only for private shares */}
          {privateShareCreated && (
            <div className="private-share-success">
              <div className="private-link-box">
                <input
                  type="text"
                  value={privateShareUrl}
                  readOnly
                  className="private-link-input"
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className={`copy-btn-inline ${copied ? 'copied' : ''}`}
                  onClick={handleCopyUrl}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {privateShareUrl.includes('#pwd=') ? (
                <p className="share-permissions__note share-password-note">
                  <FontAwesomeIcon icon={faLock} className="me-2" />
                  Password protected. The password is built into this link, so it opens directly for
                  anyone you send it to.
                </p>
              ) : null}
              <SocialShareButtons
                url={privateShareUrl}
                text={`Check out "${strategyName}" on BotSpot!`}
                className="compact"
              />
              <div className="modal-action-buttons">
                <Button variant="secondary" onClick={onHide} size="sm">
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* Social Share & Actions for marketplace */}
          {!privateShareCreated && (
            <div className="marketplace-footer">
              <SocialShareButtons
                url={marketplaceUrl}
                text={`Check out my ${isAccountShare ? 'live trading account' : 'trading strategy'} "${subjectName}" on BotSpot!`}
                label={isAccountShare ? 'Share your account' : 'Share your listing'}
                className="marketplace-share-compact"
              />
              {shareError && (
                <div className="error-message" role="alert">
                  {shareError}
                </div>
              )}
              {usesCanonicalPublication && canonicalState?.settingsUrl && (
                <a className="marketplace-view-link" href={canonicalState.settingsUrl}>
                  Publication settings
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </a>
              )}
              <div className="modal-action-buttons">
                <Button
                  variant="link"
                  className="marketplace-remove-listing"
                  onClick={handleMakePrivate}
                  disabled={isToggling}
                  size="sm"
                >
                  {isToggling
                    ? 'Removing...'
                    : isAccountShare
                      ? 'Remove from Marketplace'
                      : 'Remove listing'}
                </Button>
                <Button
                  variant="primary"
                  className="marketplace-done-button"
                  onClick={onHide}
                  size="sm"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Strategy Name Header - Elegant */}
          <div className="strategy-header">
            <div className="strategy-name">{subjectName}</div>
            <div className="strategy-subtitle">
              {isAccountShare
                ? 'Share account performance without exposing its strategies'
                : 'Choose how to share'}
            </div>
          </div>

          {usesCanonicalPublication && canonicalState?.accountChoices?.length > 1 && (
            <Form.Group className="mb-3" controlId="share-account-choice">
              <Form.Label>Brokerage account</Form.Label>
              <Form.Select
                aria-label="Brokerage account to share"
                value={publication.selectedAccountId}
                onChange={(event) => publication.setSelectedAccountId(event.target.value)}
              >
                <option value="">Choose an account</option>
                {canonicalState.accountChoices.map((choice) => (
                  <option key={choice.accountId} value={choice.accountId}>
                    {choice.displayName} · {choice.broker} · {choice.mode}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <div className="share-type-pills">
            <button
              type="button"
              className={`share-pill ${shareType === 'public' ? 'active' : ''}`}
              onClick={() => setShareType('public')}
            >
              <FontAwesomeIcon icon={faGlobe} className="pill-icon" />
              <span className="pill-label">{publicDestinationLabel}</span>
              {isInMarketplace && <span className="share-pill-status">Shared</span>}
            </button>
            <button
              type="button"
              className={`share-pill ${shareType === 'private' ? 'active' : ''}`}
              onClick={() => setShareType('private')}
            >
              <FontAwesomeIcon icon={faLock} className="pill-icon" />
              <span className="pill-label">Private Link</span>
              {privateLinkActive && <span className="share-pill-status">Link ready</span>}
            </button>
          </div>

          {shareType ? (
            <fieldset className="share-permissions">
              <legend>What people can see</legend>
              {[
                ['allowPerformanceView', 'Performance'],
                ['allowPositionsView', 'Positions'],
                ['allowFilledTradesView', 'Filled trades'],
                ['allowOpenOrdersView', 'Open orders'],
                ['allowExactQuantitiesView', 'Exact quantities'],
                ...(!isAccountShare
                  ? [
                      ['allowCodeView', 'Strategy code'],
                      ['allowClone', 'Clone strategy'],
                    ]
                  : []),
              ].map(([permission, label]) => (
                <Form.Check
                  key={permission}
                  type="switch"
                  id={`share-${permission}`}
                  checked={permissions[permission] === true}
                  onChange={(event) => {
                    setPermissionsTouched(true);
                    setLiveLinkSaved(false);
                    setPermissions((current) => {
                      const next = {
                        ...current,
                        [permission]: event.target.checked,
                      };
                      if (permission === 'allowCodeView' && !event.target.checked) {
                        next.allowClone = false;
                      }
                      return next;
                    });
                  }}
                  label={label}
                />
              ))}
              {isAccountShare ? (
                <p className="share-permissions__note">
                  Strategy names, code, revisions, and deployment details are never included.
                </p>
              ) : (
                <p className="share-permissions__note">
                  Performance comes from the selected account, but sibling strategies remain
                  private.
                </p>
              )}
            </fieldset>
          ) : null}

          {shareType === 'public' && usesCanonicalPublication ? (
            <fieldset className="share-presentation">
              <legend>Marketplace presentation</legend>
              <Form.Group controlId="marketplace-share-title">
                <Form.Label>Marketplace title</Form.Label>
                <Form.Control
                  value={marketplacePresentation.title}
                  maxLength={140}
                  onChange={(event) => {
                    setMarketplacePresentation((current) => ({
                      ...current,
                      title: event.target.value,
                    }));
                    setPresentationDirty(true);
                  }}
                />
              </Form.Group>
              <Form.Group controlId="marketplace-share-description">
                <Form.Label>Short description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={marketplacePresentation.description}
                  maxLength={1200}
                  onChange={(event) => {
                    setMarketplacePresentation((current) => ({
                      ...current,
                      description: event.target.value,
                    }));
                    setPresentationDirty(true);
                  }}
                />
              </Form.Group>
              <div className="share-presentation__row">
                <Form.Group controlId="marketplace-share-tags">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control
                    value={marketplacePresentation.tags}
                    placeholder="momentum, options"
                    onChange={(event) => {
                      setMarketplacePresentation((current) => ({
                        ...current,
                        tags: event.target.value,
                      }));
                      setPresentationDirty(true);
                    }}
                  />
                </Form.Group>
                <Form.Group controlId="marketplace-share-assets">
                  <Form.Label>Asset classes</Form.Label>
                  <Form.Control
                    value={marketplacePresentation.assetClasses}
                    placeholder="stocks, options"
                    onChange={(event) => {
                      setMarketplacePresentation((current) => ({
                        ...current,
                        assetClasses: event.target.value,
                      }));
                      setPresentationDirty(true);
                    }}
                  />
                </Form.Group>
              </div>
              <Form.Group controlId="marketplace-share-image">
                <Form.Label>Listing image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    setPresentationImage(event.target.files?.[0] || null);
                    setRemovePresentationImage(false);
                    setPresentationDirty(true);
                  }}
                />
                <Form.Text>
                  PNG, JPG, or WebP. BotSpot re-encodes the image before publishing.
                </Form.Text>
              </Form.Group>
              {canonicalState?.marketplace?.heroImageUrl ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setPresentationImage(null);
                    setRemovePresentationImage(true);
                    setPresentationDirty(true);
                  }}
                >
                  Remove current image
                </Button>
              ) : null}
              <details>
                <summary>Full listing details</summary>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={marketplacePresentation.details}
                  maxLength={12000}
                  onChange={(event) => {
                    setMarketplacePresentation((current) => ({
                      ...current,
                      details: event.target.value,
                    }));
                    setPresentationDirty(true);
                  }}
                />
              </details>
            </fieldset>
          ) : null}

          {shareType === 'private' && (
            <div className="share-private-context">
              <fieldset className="share-permissions" disabled={isToggling || publication.loading}>
                <legend>Who can access</legend>
                <Form.Group className="mb-3" controlId="private-share-recipient">
                  <Form.Label>Restrict to a BotSpot user (optional)</Form.Label>
                  <Form.Control
                    type="email"
                    value={privateRecipientEmail}
                    onChange={(event) => setPrivateRecipientEmail(event.target.value)}
                    placeholder="recipient@example.com"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="private-share-password">
                  <Form.Check
                    type="switch"
                    label="Password protect this link"
                    checked={passwordProtected}
                    onChange={(event) => {
                      setPasswordTouched(true);
                      setPasswordProtected(event.target.checked);
                    }}
                  />
                  <Form.Text>
                    {passwordProtected
                      ? 'The password is built into the link, like a Zoom link. The full link opens directly; part of the link alone does not.'
                      : 'Anyone with the link can open it.'}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="private-share-expiration">
                  <Form.Label>Expiration (optional)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={privateExpiresAt}
                    onChange={(event) => setPrivateExpiresAt(event.target.value)}
                  />
                </Form.Group>
              </fieldset>
              {shareInfoLoading ? (
                <div className="share-loading">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="ms-2">Loading...</span>
                </div>
              ) : existingPrivateShare?.shareUrl ? (
                <>
                  <div className="existing-link-section">
                    <div className="existing-link-header">
                      <span className="existing-link-label">Current private link</span>
                      <button
                        type="button"
                        className={`copy-icon-btn ${copied ? 'copied' : ''}`}
                        onClick={() => copyUrlToClipboard(existingPrivateShare.shareUrl)}
                        title={copied ? 'Copied!' : 'Copy link'}
                      >
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={existingPrivateShare.shareUrl}
                      readOnly
                      className="private-link-input"
                      onClick={(e) => e.target.select()}
                    />
                  </div>
                  <SocialShareButtons
                    url={existingPrivateShare.shareUrl}
                    text={`Check out "${strategyName}" on BotSpot!`}
                    className="compact"
                  />
                  {usesCanonicalPublication && (
                    <Button
                      variant="link"
                      className="marketplace-remove-listing"
                      onClick={publication.revokePrivateLink}
                      disabled={isToggling || publication.loading}
                      size="sm"
                    >
                      Revoke private link
                    </Button>
                  )}
                </>
              ) : privateLinkActive ? (
                <div className="existing-link-section">
                  <div className="existing-link-header">
                    <span className="existing-link-label">Private link active</span>
                  </div>
                  <p className="share-permissions__note">
                    The secret link cannot be shown again. Save settings keeps this same link.
                    Regenerate Link makes a new link and turns this one off.
                  </p>
                  <Button
                    variant="link"
                    className="marketplace-remove-listing"
                    onClick={publication.revokePrivateLink}
                    disabled={isToggling || publication.loading}
                    size="sm"
                  >
                    Revoke private link
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {/* Explanation - Only show after selecting share type */}
          {shareType && (
            <div className="share-explanation">
              {shareType === 'private' ? (
                <p>
                  Generate a secret link. It is password protected by default, with the password
                  built into the link, so you send one link. Anyone who receives or forwards the
                  link can open it until you turn it off. You can also limit it to one BotSpot user
                  or set an end date.
                </p>
              ) : (
                <p>
                  {isAccountShare
                    ? `Share this account on ${publicDestinationLabel}. Strategy identities, balances, account numbers, and credentials stay private.`
                    : isInMarketplace
                      ? 'This strategy is already available in the Marketplace.'
                      : !permissions.allowCodeView
                        ? 'Share your strategy in the Marketplace right away without exposing its source code.'
                        : 'Share your strategy in the Marketplace. Other users can discover and use it right away.'}
                </p>
              )}
            </div>
          )}

          {shareType === 'public' && isInMarketplace && (
            <div className="share-marketplace-existing-actions">
              {marketplaceUrl && (
                <>
                  <a
                    href={marketplaceUrl}
                    className="marketplace-view-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {isAccountShare ? 'View on Marketplace' : 'View your listing'}
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </a>
                  <SocialShareButtons
                    url={marketplaceUrl}
                    text={`Check out my ${isAccountShare ? 'live trading account' : 'trading strategy'} "${subjectName}" on BotSpot!`}
                    label={isAccountShare ? 'Share your account' : 'Share your listing'}
                    className="marketplace-share-compact"
                  />
                </>
              )}
              <Button
                type="button"
                variant="link"
                className="marketplace-remove-listing"
                onClick={handleMakePrivate}
                disabled={isToggling || publication.loading}
                size="sm"
              >
                {isToggling
                  ? 'Removing...'
                  : isAccountShare
                    ? 'Remove from Marketplace'
                    : 'Remove listing'}
              </Button>
            </div>
          )}

          {/* Code Protection Toggle - Only for regular public shares. Listings are protected by default. */}
          {shareType === 'public' && !isInMarketplace && !isAccountShare && (
            <div className="protect-code-section">
              <div className="protect-code-toggle">
                <Form.Check
                  type="switch"
                  id="protect-code-toggle"
                  checked={!permissions.allowCodeView}
                  onChange={(e) => {
                    setPermissionsTouched(true);
                    setPermissions((current) => ({
                      ...current,
                      allowCodeView: !e.target.checked,
                      allowClone: e.target.checked ? false : current.allowClone,
                    }));
                  }}
                  label={
                    <span className="protect-label">
                      <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                      Protect my code
                    </span>
                  }
                />
              </div>
              <div className={`protect-code-info ${!permissions.allowCodeView ? 'protected' : ''}`}>
                {!permissions.allowCodeView ? (
                  <>
                    <FontAwesomeIcon icon={faLock} className="info-icon protected" />
                    <span>
                      Users can <strong>run and deploy</strong> your strategy, but{' '}
                      <strong>cannot view or copy</strong> the code.
                    </span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCode} className="info-icon" />
                    <span>Users can view, copy, and modify your code freely.</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {shareError && (
            <div className="error-message" role="alert">
              {shareError}
            </div>
          )}
          {usesCanonicalPublication && canonicalState?.settingsUrl && (
            <a className="marketplace-view-link" href={canonicalState.settingsUrl}>
              Publication settings
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </a>
          )}
          {canSaveLiveLink && accessChangeNeedsNewLink && (
            <p className="share-permissions__note share-live-link-note">
              A new password, expiration, or recipient needs a new link. Use Regenerate Link.
            </p>
          )}
          {canSaveLiveLink && liveLinkSaved && (
            <p className="share-live-link-saved" role="status">
              {LIVE_LINK_SAVED}
            </p>
          )}
          <div className="modal-action-buttons">
            {canSaveLiveLink && (
              <Button
                variant="primary"
                onClick={handleSaveLiveLinkSettings}
                disabled={
                  isToggling || savingLiveLink || publication.loading || accessChangeNeedsNewLink
                }
                size="sm"
              >
                Save settings
              </Button>
            )}
            <Button
              variant={canSaveLiveLink ? 'secondary' : 'primary'}
              onClick={() => {
                // Pass true directly if regenerating (don't rely on async state)
                // Canonical publication reads intentionally do not return the
                // existing secret URL. The active-state flag is authoritative:
                // requesting another link must rotate it so this response can
                // return the newly issued token exactly once.
                const shouldRegenerate = shareType === 'private' && privateLinkActive;
                handleToggleVisibility(shouldRegenerate);
              }}
              disabled={
                isToggling ||
                savingLiveLink ||
                publication.loading ||
                !shareType ||
                (usesCanonicalPublication &&
                  canonicalState?.selectionRequired &&
                  !publication.selectedAccountId)
              }
              size="sm"
            >
              {isToggling
                ? 'Working...'
                : !shareType
                  ? 'Choose an option above'
                  : shareType === 'private'
                    ? privateLinkActive
                      ? 'Regenerate Link'
                      : 'Generate Link'
                    : isInMarketplace && isPublic
                      ? 'Save changes'
                      : isAccountShare
                        ? 'Share on Marketplace'
                        : 'Share to Marketplace'}
            </Button>
            <Button variant="secondary" onClick={onHide} size="sm">
              Cancel
            </Button>
          </div>
        </>
      )}
    </ContentModal>
  );
};

export default ShareStrategyModal;
